import React from "react";
import Head from "next/head";
import { NextPage, NextPageContext } from "next";

import BehaviorIcon from "src/components/icons/BehaviorIcon";
import DatasetIcon from "src/components/icons/DatasetIcon";
import IndexLogo from "src/components/logos/IndexLogo";
import Link from "next/link";
import OpenInNewIcon from "src/components/icons/OpenInNewIcon";
import PlatformExploreBanner from "src/components/sales/PlaformExploreBanner";
import SimulationIcon from "src/components/icons/SimulationIcon";
import TypeTile from "src/components/index/TypeTile";
import wp from "../../lib/wp";
import { contentTypes } from "src/components/index/BrowseAndExploreBanner";
import { socialMetadata } from "src/components/layout/Head";

const PAGE_ID = 391;

const Index: NextPage = () => (
  <>
    <Head>
        {socialMetadata({
          title: "HASH - Browse Simulations",
          description: "Simulation modeling community and package manager",
        })}
    </Head>
    <div className="platform-page index">
      <div className="mainsite-header with-breadcrumb with-image">
        <div className="container">
          <div className="header-image">
            <IndexLogo />
          </div>
          <div className="header-text">
            <div className="mainsite-breadcrumb">
              <Link href="/platform">
                <a>
                  Platform <span>{">"}</span>
                </a>
              </Link>
            </div>
            <div className="title-with-button">
              <div className="mainsite-title">
                <h1 className="heading-1">
                  Simulation Community
                </h1>
                <p className="large-text">
                  Simulation community and package manager
                </p>
              </div>
              <Link href="/index">
                <a target="_blank">
                  <button className="primary-button">
                    Browse <span>&nbsp;simulations</span> <OpenInNewIcon />
                  </button>
                </a>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="platform-content">
        <div className="container">
          <h3 className="heading-4">OVERVIEW</h3>

          <div className="paras">
            <div className="para">
              <h2 className="para-title">Community</h2>
              <p>
                Welcome to the world’s largest simulation community. We bring
                together <strong>10,000+ simulation creators</strong> alongside:
              </p>
              <ul>
                <li>
                  <span className="icon-wrapper">
                    <SimulationIcon />
                  </span>
                  <strong>4500+</strong>&nbsp;simulations
                </li>
                <li>
                  <span className="icon-wrapper">
                    <DatasetIcon />
                  </span>
                  <strong>350+</strong>&nbsp;datasets
                </li>
                <li>
                  <span className="icon-wrapper behavior">
                    <BehaviorIcon />
                  </span>
                  <strong>100+</strong>&nbsp;behavior sets
                </li>
              </ul>
              <p>
                This published content is just the tip of the iceberg of what
                creators have built with HASH, and is available for anybody to
                remix and share.
              </p>
            </div>

            <div className="para">
              <h2 className="para-title">Package and Data Registry</h2>
              <p>
                <strong>HASH</strong> acts as a simulation package and data
                registry, powering dependencies and search in{" "}
                <Link href="/platform/core">
                  <a>HASH Core</a>
                </Link>{" "}
                and enabling users to keep simulation models, behaviors,
                and underlying data up-to-date.
              </p>
            </div>

            <div className="para">
              <h2 className="para-title">Marketplace</h2>
              <p>
                Users can also buy and sell simulations as well as their
                component parts on <strong>HASH</strong>.
              </p>
              <p>
                To find out more about publishing paid content, such as
                proprietary data, please{" "}
                <Link href="/contact">
                  <a>contact us</a>
                </Link>
                .
              </p>
            </div>
          </div>
        </div>

        <div className="page-footer-cta purple index">
          <div className="signup-background-icon">
            <IndexLogo />
          </div>
          <div className="container">
            <h3 className="heading-4">DISCOVER WHAT'S AVAILABLE</h3>

            <div className="homepage-type-cards normal-text">
              {contentTypes.map((type) => (
                <TypeTile type={type} key={type.type} />
              ))}
            </div>
          </div>
        </div>

        <PlatformExploreBanner exclude="Index" />
      </div>
    </div>
  </>
);

Index.getInitialProps = async (
  ctx: NextPageContext & {
    query: any;
    signup: () => void;
  }
) => {
  let page;
  try {
    page = await wp.pages().id(PAGE_ID);
  } catch (error) {
    console.error(error);
  }
  return { signup: ctx.signup, page };
};

export default Index;
